import { format, formatDistanceToNow, differenceInHours, isYesterday } from 'date-fns';

const API_BASE_URL =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

const BASE = `${API_BASE_URL}/api/my-tenders`;

function headers(tenantId) {
  return {
    'Content-Type': 'application/json',
    'x-tenant-id': tenantId,
  };
}

export async function fetchMyTendersConfig(tenantId) {
  if (!tenantId) return { enabled: false };
  const res = await fetch(`${BASE}/config`, {
    headers: { 'x-tenant-id': tenantId },
    credentials: 'include',
  });
  if (!res.ok) return { enabled: false };
  return res.json();
}

export async function fetchMyTendersSummary(tenantId, categories = []) {
  const qs =
    categories.length > 0
      ? `?categories=${encodeURIComponent(categories.join(','))}`
      : '';
  const res = await fetch(`${BASE}/summary${qs}`, {
    headers: { 'x-tenant-id': tenantId },
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Summary load failed');
  return res.json();
}

export async function fetchMyTendersAugment(tenantId, dealIds) {
  const ids = (dealIds || []).filter(Boolean);
  if (!tenantId || ids.length === 0) return {};

  // BE hard-caps at 1000 and chunks internally at 100; keep FE batches ≤200
  // so a full page never silently truncates past the cap.
  const CHUNK = 200;
  const merged = {};
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    const res = await fetch(`${BASE}/augment`, {
      method: 'POST',
      headers: headers(tenantId),
      credentials: 'include',
      body: JSON.stringify({ deal_ids: chunk }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body?.error || `Augment failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    const data = await res.json();
    Object.assign(merged, data?.records || {});
  }
  return merged;
}

export async function fetchMyTendersTimeline(tenantId, dealId) {
  if (!tenantId || !dealId) return { timeline: [] };
  const res = await fetch(
    `${BASE}/timeline?deal_id=${encodeURIComponent(dealId)}`,
    {
      headers: { 'x-tenant-id': tenantId },
      credentials: 'include',
    }
  );
  if (!res.ok) return { timeline: [] };
  return res.json();
}

/**
 * Fire-and-forget view recording. Never throws.
 * deal_id must be the exact API value — do not normalise.
 */
export async function logDealView(tenantId, dealId) {
  if (!tenantId || !dealId) return;
  try {
    const res = await fetch(`${BASE}/view`, {
      method: 'POST',
      headers: headers(tenantId),
      credentials: 'include',
      body: JSON.stringify({ deal_id: dealId }),
    });
    if (res.status === 401 || res.status === 403) return;
    if (res.status === 400) {
      const body = await res.json().catch(() => ({}));
      console.warn('[MyTenders] view rejected:', body?.error || 'deal_id required');
      return;
    }
    if (!res.ok) return;
  } catch {
    // swallow network errors — never block deal detail
  }
}

export async function setDealValueOverride(tenantId, dealId, value) {
  const res = await fetch(`${BASE}/value-override`, {
    method: 'POST',
    headers: headers(tenantId),
    credentials: 'include',
    body: JSON.stringify({ deal_id: dealId, value }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || 'Value override failed');
  }
  return res.json();
}

export async function clearDealValueOverride(tenantId, dealId) {
  const res = await fetch(
    `${BASE}/value-override?deal_id=${encodeURIComponent(dealId)}`,
    {
      method: 'DELETE',
      headers: { 'x-tenant-id': tenantId },
      credentials: 'include',
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || 'Clear override failed');
  }
  return res.json();
}

export async function setDealStatus(tenantId, dealId, status, note) {
  const res = await fetch(`${BASE}/status`, {
    method: 'POST',
    headers: headers(tenantId),
    credentials: 'include',
    body: JSON.stringify({
      deal_id: dealId,
      status,
      ...(note ? { note } : {}),
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || 'Status update failed');
  }
  return res.json();
}

export async function clearDealStatus(tenantId, dealId) {
  const res = await fetch(
    `${BASE}/status?deal_id=${encodeURIComponent(dealId)}`,
    {
      method: 'DELETE',
      headers: { 'x-tenant-id': tenantId },
      credentials: 'include',
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || 'Status clear failed');
  }
  return res.json();
}

/** Status dot vocabulary for Live Radar + My Tenders (6 settable + untouched). */
export function getDealStatusDisplay(status) {
  if (!status) {
    return { symbol: '○', color: '#9ca3af', label: 'Untouched' };
  }
  const map = {
    reviewing: { symbol: '◔', color: '#93c5fd', label: 'Reviewing' },
    pursuing: { symbol: '◐', color: '#00C2C2', label: 'Pursuing' },
    submitted: { symbol: '◧', color: '#1e40af', label: 'Submitted' },
    passed: { symbol: '●', color: '#9f5f5f', label: 'Passed' },
    won: { symbol: '✓', color: '#22c55e', label: 'Won' },
    lost: { symbol: '✗', color: '#4b5563', label: 'Lost' },
  };
  return map[status] || { symbol: '○', color: '#9ca3af', label: status || 'Unknown' };
}

export const DEAL_STATUS_PICKER_OPTIONS = [
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'pursuing', label: 'Pursuing' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'passed', label: 'Passed' },
  { value: 'untouched', label: 'Untouched (clear)' },
];

export function resolveDealId(deal) {
  if (!deal) return null;
  const id = deal.deal_id ?? deal.id ?? deal.tender_id ?? deal.fit_deal_id ?? null;
  return id != null && id !== '' ? String(id) : null;
}

/** Merge server augment without clobbering newer optimistic status rows. */
export function mergeAugmentRecords(prev = {}, incoming = {}, { preserveDealIds = null } = {}) {
  const preserve = preserveDealIds instanceof Set ? preserveDealIds : new Set();
  const next = { ...prev };

  for (const [dealId, serverRec] of Object.entries(incoming || {})) {
    if (preserve.has(dealId)) continue;

    const localRec = prev[dealId];
    const localStatusAt = localRec?.status_set_at ? new Date(localRec.status_set_at).getTime() : 0;
    const serverStatusAt = serverRec?.status_set_at ? new Date(serverRec.status_set_at).getTime() : 0;
    const localValueAt = localRec?.value_override_set_at
      ? new Date(localRec.value_override_set_at).getTime()
      : 0;
    const serverValueAt = serverRec?.value_override_set_at
      ? new Date(serverRec.value_override_set_at).getTime()
      : 0;

    let merged = { ...(localRec || {}), ...serverRec };
    if (localRec && localStatusAt > serverStatusAt) {
      merged = { ...merged, ...localRec };
    }
    if (localRec && localValueAt > serverValueAt) {
      merged = {
        ...merged,
        value_override: localRec.value_override,
        value_override_set_at: localRec.value_override_set_at,
        value_override_by_user_id: localRec.value_override_by_user_id,
        value_override_by_user_name: localRec.value_override_by_user_name,
      };
    }
    next[dealId] = merged;
  }

  return next;
}

export function collectDealIds(deals) {
  const ids = new Set();
  for (const d of deals || []) {
    const id = resolveDealId(d);
    if (id) ids.add(id);
  }
  return Array.from(ids);
}

/** Light teal for Reviewing/Pursuing; dark blue for Submitted; darker teal for Won; light red for Passed/Lost. */
export function getStatusBadgeClasses(status) {
  if (status === 'won') {
    return 'bg-[#00C2C2]/30 text-[#0A1A2F] border-[#00C2C2] hover:bg-[#00C2C2]/40';
  }
  if (status === 'reviewing' || status === 'pursuing') {
    return 'bg-[#ECFFFE] text-[#0A1A2F] border-[#00C2C2]/35 hover:bg-[#ECFFFE]/80';
  }
  if (status === 'submitted') {
    return 'bg-blue-50 text-blue-900 border-blue-300 hover:bg-blue-100/80';
  }
  if (status === 'passed' || status === 'lost') {
    return 'bg-red-50 text-red-800 border-red-200 hover:bg-red-100/80';
  }
  return 'bg-gray-50 text-[#5C6B7A] border-gray-200 hover:bg-gray-100';
}

export function getDealValueDisplay(deal, augment) {
  const rec = augment || {};

  // A rep-entered override is in the tenant's pipeline currency — USD for every
  // tenant in tenant_my_tenders_config today — and it is what the Pipeline
  // total sums, so it must be labelled the same way.
  const override = rec.value_override;
  const overrideNum = Number(override);
  if (override != null && Number.isFinite(overrideNum) && overrideNum > 0) {
    return {
      value: overrideNum, currency: 'USD', isUSD: true,
      nativeValue: null, nativeCurrency: null,
      isOverride: true,
      overrideBy: rec.value_override_by_user_name ?? null,
      overrideAt: rec.value_override_set_at ?? null,
    };
  }

  // [2026-09-03] A USD FIGURE MUST NOT WEAR THE NATIVE CURRENCY'S LABEL.
  // This read deal_value_usd first and stamped it with deal.currency, so a
  // Colombian tender worth 1,090,000 COP rendered as "COP 272.5" — its USD
  // value under the local code. See utils/procurementValueUsd.js: "never treat
  // native deal_value as USD unless currency is USD."
  const usd = Number(deal?.deal_value_usd);
  if (Number.isFinite(usd) && usd > 0) {
    const nativeRaw = Number(deal?.deal_value ?? deal?.estimated_value);
    const nativeCurrency = deal?.currency || null;
    return {
      value: usd, currency: 'USD', isUSD: true,
      // Kept so the cell can show the tender's own figure on hover.
      nativeValue: Number.isFinite(nativeRaw) && nativeRaw > 0 ? nativeRaw : null,
      nativeCurrency: nativeCurrency && nativeCurrency !== 'USD' ? nativeCurrency : null,
      isOverride: false, overrideBy: null, overrideAt: null,
    };
  }

  // No converted figure on file: show the NATIVE amount under its OWN code.
  // This is correct, not a gap — it is the tender's stated value.
  const currency = deal?.currency || '';
  for (const raw of [deal?.deal_value, deal?.estimated_value, deal?.value]) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) {
      return {
        value: n, currency, isUSD: false,
        nativeValue: null, nativeCurrency: null,
        isOverride: false, overrideBy: null, overrideAt: null,
      };
    }
  }

  return {
    value: null, currency: '', isUSD: false,
    nativeValue: null, nativeCurrency: null,
    isOverride: false, overrideBy: null, overrideAt: null,
  };
}

export function formatDealValueDisplay({ value, currency, isUSD }) {
  if (value == null) return '—';
  // USD renders as $ with no decimals, matching DealValueDisplay elsewhere in
  // Procurement. A native amount keeps its own code and its own precision.
  if (isUSD) return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const curr = currency || '';
  return curr ? `${curr} ${value.toLocaleString()}` : value.toLocaleString();
}

/** The tender's own figure, for a hover title. '' when there is nothing to add. */
export function formatNativeValueHint({ nativeValue, nativeCurrency }) {
  if (!nativeValue || !nativeCurrency) return '';
  return `${nativeCurrency} ${Number(nativeValue).toLocaleString()}`;
}

/** Compact USD for the My Deals Total Deal Value card. Exact figure belongs in `title`. */
export function formatUSDCompact(n) {
  if (n == null) return '$0';
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return '$0';
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
  return `$${Math.round(num).toLocaleString()}`;
}

/** Relative (<24h) or short date for status_set_at. */
export function formatStatusUpdatedAt(at) {
  if (!at) return null;
  try {
    const d = new Date(at);
    if (!Number.isFinite(d.getTime())) return null;
    if (differenceInHours(new Date(), d) < 24) {
      return formatDistanceToNow(d, { addSuffix: true });
    }
    if (isYesterday(d)) return 'yesterday';
    return format(d, 'MMM d');
  } catch {
    return null;
  }
}
